'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetMarket, useGetOutcomes } from '@morfi/backend-sdk-v2/client';
import type { MarketIdentityData } from '@/components/market/identity/market-identity';
import { estimateOutcomeTokenSpotValueFromStringsD6 } from '@/lib/gifts/outcome-token-spot-value';

/**
 * Live snapshot of an outcome-token mint — surface label, parent market title,
 * and current spot price so gift surfaces can render an indicative value.
 *
 * The backend returns the marginal price as a D9 integer string. Consumers
 * must pass it to shared market math; it is not a JS number or sell quote.
 */
export interface OutcomeTokenSnapshot {
  identity?: MarketIdentityData | undefined;
  /** Outcome label (e.g. "Yes"). Null until the outcome resolves. */
  outcomeLabel: string | null;
  /** Parent market title (e.g. "Will BTC hit 100k?"). Null until the market resolves. */
  marketTitle: string | null;
  /**
   * Outcome marginal spot price as an exact D9 integer string. `null` until a
   * valid outcome row resolves.
   */
  currentPriceD9: string | null;
  /** True once both outcome + market lookups have settled with non-null data. */
  isLive: boolean;
}
const EMPTY_SNAPSHOT: OutcomeTokenSnapshot = {
  outcomeLabel: null,
  marketTitle: null,
  currentPriceD9: null,
  isLive: false
};
const OUTCOME_FIELDS = ['label', 'price', 'market'] as const;

/**
 * Resolve `{ outcomeLabel, marketTitle, currentPriceD9, isLive }` for an
 * outcome-token mint. Pass `null` to skip the network calls (e.g. when the
 * gift is USDC). The hook keys into the SDK v2 cache so multiple gift rows
 * showing the same mint share a single round trip.
 *
 * Migrated to SDK v2 (SPEC 009 FILE 14): the legacy `useGetOutcome` (by mint)
 * is collapsed into `useGetOutcomes({mints: <mint>})` — the SDK returns a
 * single-row list scoped by mint. The parent market is then resolved via
 * `useGetMarket({pubkey})`.
 */
export function useOutcomeTokenSnapshot(mintPubkey) {
  const $ = _c(13);
  const enabled = mintPubkey !== null && mintPubkey.length > 0;
  const t0 = enabled ? mintPubkey : "";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...OUTCOME_FIELDS];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t0) {
    t2 = {
      mints: t0,
      fields: t1
    };
    $[1] = t0;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const outcomesQuery = useGetOutcomes(t2);
  const outcomeRow = outcomesQuery.data?.data?.[0];
  const marketPubkey = outcomeRow?.market ?? "";
  const marketEnabled = marketPubkey.length > 0;
  const t3 = marketEnabled ? marketPubkey : "";
  let t4;
  if ($[3] !== t3) {
    t4 = {
      pubkey: t3
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const marketQuery = useGetMarket(t4);
  if (!enabled) {
    return EMPTY_SNAPSHOT;
  }
  if (outcomeRow === undefined) {
    return EMPTY_SNAPSHOT;
  }
  const outcomeLabel = outcomeRow.label ?? null;
  const priceRaw = outcomeRow.price;
  let t5;
  if ($[5] !== priceRaw) {
    t5 = priceRaw !== undefined && estimateOutcomeTokenSpotValueFromStringsD6("0", priceRaw) !== null ? priceRaw : null;
    $[5] = priceRaw;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const currentPriceD9 = t5;
  const marketTitle = marketQuery.data?.title ?? null;
  const t6 = currentPriceD9 !== null && marketTitle !== null;
  let t7;
  if ($[7] !== currentPriceD9 || $[8] !== marketQuery.data || $[9] !== marketTitle || $[10] !== outcomeLabel || $[11] !== t6) {
    t7 = {
      identity: marketQuery.data,
      outcomeLabel,
      marketTitle,
      currentPriceD9,
      isLive: t6
    };
    $[7] = currentPriceD9;
    $[8] = marketQuery.data;
    $[9] = marketTitle;
    $[10] = outcomeLabel;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}